import React, { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { useTheme } from '../contexts/ThemeContext';

interface VideoBackgroundProps {
  src: string;
  className?: string;
  fallbackImage?: string;
}

export const VideoBackground: React.FC<VideoBackgroundProps> = ({
  src,
  className = '',
  fallbackImage
}) => {
  const { isDarkMode } = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const initializeVideo = () => {
      // Check if HLS is supported
      if (Hls.isSupported()) {
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 90,
          maxBufferLength: 30,
          maxMaxBufferLength: 600,
          maxBufferSize: 60 * 1000 * 1000,
          maxBufferHole: 0.5,
          highBufferWatchdogPeriod: 2,
          nudgeOffset: 0.1,
          nudgeMaxRetry: 3,
          maxFragLookUpTolerance: 0.25,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: Infinity,
          enableSoftwareAES: true,
          manifestLoadingTimeOut: 10000,
          manifestLoadingMaxRetry: 1,
          manifestLoadingRetryDelay: 1000,
          levelLoadingTimeOut: 10000,
          levelLoadingMaxRetry: 4,
          levelLoadingRetryDelay: 1000,
          fragLoadingTimeOut: 20000,
          fragLoadingMaxRetry: 6,
          fragLoadingRetryDelay: 1000,
          startFragPrefetch: false,
          appendErrorMaxRetry: 3
        });
        
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.playbackRate = 0.75;
          video.play().catch(() => {
            // Silently handle video play errors
          });
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
                break;
            }
          }
        });
      } 
      // For browsers with native HLS support (like Safari)
      else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = src;
        video.addEventListener('loadedmetadata', () => {
          video.playbackRate = 0.75;
          video.play().catch(() => {
            // Silently handle video play errors
          });
        });
        video.addEventListener('error', () => {
          // Silently handle video load errors
        });
      }
      // Fallback for unsupported browsers
      else {
        video.src = src;
        video.addEventListener('loadedmetadata', () => {
          video.playbackRate = 0.75;
          video.play().catch(() => {
            // Silently handle video play errors
          });
        });
        video.addEventListener('error', () => {
          // Silently handle video load errors
        });
      }
    };

    initializeVideo();

    // Cleanup function
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src]);

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
      <video
        ref={videoRef}
        className="w-full h-full object-cover opacity-30"
        autoPlay={true}
        muted={true}
        loop={true}
        playsInline={true}
        poster={fallbackImage}
        style={{ filter: isDarkMode ? 'brightness(0.5) contrast(1.1)' : 'brightness(0.7) contrast(1.05)' }}
      />
      
      {/* Overlay for better text readability */}
      <div className={`absolute inset-0 bg-gradient-to-br transition-all duration-300 pointer-events-none ${
        isDarkMode 
          ? 'from-black/40 via-black/10 to-black/20' 
          : 'from-black/30 via-black/5 to-black/15'
      }`} />
    </div>
  );
};